import React, { useRef, useState } from "react";
import { storage } from "../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { ArrowUpTrayIcon, PaperClipIcon } from "@heroicons/react/20/solid";
import { showAlert } from "../../alertService";
import { sanitizeFilename } from "../../utils/sanitizeFilename";

const ACCEPTED_MIME = ["application/pdf", "image/jpeg", "image/png"];
const ACCEPTED_ATTR = "application/pdf,image/jpeg,image/png";
const MAX_BYTES = 10 * 1024 * 1024;

export default function DocumentUploader({
  storagePathPrefix,
  buttonLabel = "Subir documento",
  changingLabel = "Cambiar documento",
  hasExisting = false,
  disabled = false,
  withDownloadUrl = true,
  iconOnly = false,
  iconTooltip = "Adjuntar archivo",
  onUploaded,
}) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const [dragging, setDragging] = useState(false);

  const handleClick = () => {
    if (disabled || progress !== null) return;
    inputRef.current?.click();
  };

  const processFile = async (file) => {
    if (!file) return;
    if (!ACCEPTED_MIME.includes(file.type)) {
      showAlert("Solo se permiten archivos PDF, JPG o PNG.", "error");
      return;
    }
    if (file.size > MAX_BYTES) {
      showAlert("El archivo supera el límite de 10 MB.", "error");
      return;
    }
    if (!storagePathPrefix) {
      showAlert("Falta el path de Storage para subir el archivo.", "error");
      return;
    }

    const sanitized = sanitizeFilename(file.name);
    const path = `${storagePathPrefix.replace(/\/+$/, "")}/${Date.now()}-${sanitized}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type });

    setProgress(0);
    try {
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            const pct = snap.totalBytes ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100) : 0;
            setProgress(pct);
          },
          reject,
          resolve,
        );
      });

      const url = withDownloadUrl ? await getDownloadURL(storageRef) : "";
      onUploaded?.({
        url,
        storagePath: path,
        filename: file.name,
        fileSize: file.size,
        mimeType: file.type,
      });
    } catch (err) {
      console.error("DocumentUploader error:", err);
      showAlert("No se pudo subir el archivo. Intenta de nuevo.", "error");
    } finally {
      setProgress(null);
    }
  };

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    await processFile(file);
  };

  const handleDragOver = (e) => {
    if (disabled || progress !== null) return;
    e.preventDefault();
    e.stopPropagation();
    if (!dragging) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (disabled || progress !== null) return;
    const file = e.dataTransfer?.files?.[0];
    await processFile(file);
  };

  const uploading = progress !== null;
  const label = uploading
    ? `Subiendo ${progress ?? 0}%`
    : hasExisting
      ? changingLabel
      : buttonLabel;
  const isDisabled = disabled || uploading;

  if (iconOnly) {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          disabled={isDisabled}
          title={uploading ? label : `${iconTooltip} (también puedes arrastrar un archivo aquí)`}
          aria-label={iconTooltip}
          className={
            "inline-flex h-7 w-7 items-center justify-center rounded transition-colors " +
            (dragging ? "bg-[#1b4d2e]/10 ring-2 ring-[#1b4d2e] text-[#1b4d2e] " : "") +
            (isDisabled
              ? "opacity-50 cursor-not-allowed text-zinc-400"
              : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100")
          }
        >
          <PaperClipIcon className="h-4 w-4" />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_ATTR}
          onChange={handleChange}
          style={{ display: "none" }}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        disabled={isDisabled}
        title={uploading ? label : "Selecciona un archivo o arrástralo aquí"}
        className={
          "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors " +
          (dragging
            ? "border-[#1b4d2e] bg-[#1b4d2e]/5 text-[#1b4d2e] ring-2 ring-[#1b4d2e]/30 "
            : "border-zinc-300 bg-white text-zinc-700 ") +
          (isDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-zinc-50")
        }
      >
        <ArrowUpTrayIcon className="h-4 w-4 text-zinc-400" />
        {dragging ? "Suelta el archivo aquí" : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_ATTR}
        onChange={handleChange}
        style={{ display: "none" }}
      />
    </>
  );
}
